import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { ChangeEvent, memo, useEffect, useMemo } from 'react';
import { CompanyService } from '../../../service/CompanyService';
import { CompanySpacesService } from '../../../service/CompanySpacesService';
import { UserService } from '../../../service/UserService';
import { useAuthStore } from '../../../store/storeAuth';
import useFlowsStore from '../../../store/storeFlows';
import { additionalOptionsArray } from '../../AiConfigs/constants';
import KitModal from '../../UI/KitModal';

interface MemoryPodFormData {
  query: string;
  searchResult: string;
  model: string;
  systemAi: string;
  topK: number;
  searchType: string;
  returnRaw: boolean;
}

type MemoryPodChangeEvent =
  | SelectChangeEvent<string>
  | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

type Props = {
  onHide: () => void;
  show: boolean;
  formData: MemoryPodFormData;
  onChange: (event: MemoryPodChangeEvent) => void;
};

type TechModel = {
  type: string;
  isDeprecated?: boolean;
  model: string;
  displayName?: string;
  disabledByCompany?: boolean;
};

type TechSystem = {
  system: string;
  models?: TechModel[];
};

const SettingsModal = ({ onHide, show, formData: values, onChange }: Props) => {
  const {
    technologies,
    companySpaceTechnologies,
    setTechnologies,
    setCompanySpaceTechnologies,
    user,
  } = useAuthStore(state => state);
  const { spaceId } = useFlowsStore();
  const isDarkTheme = user?.theme === 'dark';
  const textPrimary = isDarkTheme ? '#F9FAFB' : '#101828';
  const textSecondary = isDarkTheme ? '#D0D5DD' : '#475467';
  const borderDefault = isDarkTheme ? '#475467' : '#EAECF0';
  const sectionTitleSx = {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.15px',
    color: isDarkTheme ? '#F9FAFB' : '#000000',
  };
  const fieldLabelSx = {
    display: 'block',
    mb: 0.5,
    color: textSecondary,
    fontSize: 14,
  };
  const fieldControlSx = { mt: 0.5, borderRadius: '4px' };

  useEffect(() => {
    if (!show) return;

    const fetchAllTechnologies = async () => {
      try {
        if (spaceId) {
          const res = await CompanySpacesService.getSpaceTechnologies(spaceId);
          setCompanySpaceTechnologies(res.data);
        }
        if (technologies.length === 0) {
          const data = user?.selectedCompanyId
            ? await CompanyService.getCompanyTechnologies(
                user.selectedCompanyId,
              )
            : await UserService.getTechnologies();
          setTechnologies(data.data);
        }
      } catch (error) {
        console.log('Error fetching technologies:', error);
      }
    };

    fetchAllTechnologies();
  }, [spaceId, show, user?.selectedCompanyId]);

  const models = useMemo(() => {
    const systemsToUse: TechSystem[] =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;

    const openAiSystem = systemsToUse?.find(s => s.system === 'openai');

    return (
      openAiSystem?.models
        ?.filter(m => m.type === 'Text' && !m.isDeprecated)
        ?.map(m => ({
          value: m.model,
          label: m.displayName || m.model,
          disabled: m.disabledByCompany ?? false,
        })) ?? []
    );
  }, [technologies, companySpaceTechnologies, spaceId]);

  const additionalOptionsArrayFiltered = useMemo(() => {
    return additionalOptionsArray.map(option => ({
      ...option,
      disabled: option.value !== 'OpenAI',
    }));
  }, []);

  return (
    <KitModal
      fullWidth
      dialogContentDividers={false}
      sx={{
        '& .MuiPaper-root': {
          position: 'relative',
          overflow: 'hidden',
          maxWidth: '520px',
          borderRadius: '4px',
          border: `1px solid ${borderDefault}`,
          boxShadow:
            '0px 3px 1px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 2px 0px rgba(0,0,0,0.2)',
        },
        '& .MuiDialogContent-root': {
          padding: '24px',
          borderTop: 'none',
        },
        '& .MuiDialogActions-root': { padding: '16px' },
      }}
      showCloseButton={false}
      onHide={onHide}
      show={show}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            pb: 1,
            borderBottom: `1px solid ${borderDefault}`,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.5,
              letterSpacing: '0.15px',
              color: textPrimary,
            }}
          >
            Settings
          </Typography>
          <IconButton
            aria-label='Close'
            size='small'
            onClick={onHide}
            sx={{ color: textSecondary }}
          >
            <X size={24} />
          </IconButton>
        </Box>

        <Stack spacing={2}>
          <FormControl fullWidth variant='outlined'>
            <FormLabel sx={fieldLabelSx} htmlFor='searchType'>
              Search Type
            </FormLabel>
            <Select
              id='searchType'
              name='searchType'
              className='nodrag'
              fullWidth
              value={values.searchType}
              onChange={onChange}
              sx={fieldControlSx}
            >
              <MenuItem value='similarity'>Similarity Search</MenuItem>
              <MenuItem value='similarity-ner'>
                Similarity Search (NER)
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth variant='outlined'>
            <FormLabel sx={fieldLabelSx} htmlFor='systemAi'>
              System
            </FormLabel>
            <Select
              id='systemAi'
              name='systemAi'
              className='nodrag'
              fullWidth
              value={values.systemAi}
              onChange={onChange}
              sx={fieldControlSx}
            >
              {additionalOptionsArrayFiltered.map(opt => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                >
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant='outlined'>
            <FormLabel sx={fieldLabelSx} htmlFor='model'>
              Ai Model
            </FormLabel>
            <Select
              id='model'
              name='model'
              className='nodrag'
              fullWidth
              value={values.model}
              onChange={onChange}
              sx={fieldControlSx}
            >
              {models.map(opt => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant='outlined'>
            <FormLabel sx={fieldLabelSx} htmlFor='topK'>
              Top K
            </FormLabel>
            <TextField
              id='topK'
              type='number'
              name='topK'
              className='nodrag'
              value={values.topK}
              onChange={onChange}
              fullWidth
              sx={{ mt: 0.5 }}
            />
          </FormControl>
        </Stack>
      </Stack>
    </KitModal>
  );
};

export default memo(SettingsModal);
